import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  UncontrolledDropdown,
  DropdownMenu,
  DropdownToggle,
  Table,
  Button,
  DropdownItem,
  UncontrolledTooltip,
  Badge,
} from 'reactstrap';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import ChevronDownIcon from 'mdi-react/ChevronDownIcon';
import ChevronRightIcon from 'mdi-react/ChevronRightIcon';
import DownloadOutlineIcon from 'mdi-react/DownloadOutlineIcon';
import AccountPlusOutlineIcon from 'mdi-react/AccountPlusOutlineIcon';
import AccountQuestionOutlineIcon from 'mdi-react/AccountQuestionOutlineIcon';
import AccountMultipleCheckIcon from 'mdi-react/AccountMultipleCheckIcon';
import { reduxForm } from 'redux-form';
import PlusIcon from 'mdi-react/PlusIcon';
import MinusIcon from 'mdi-react/MinusIcon';
import CommentTextOutlineIcon from 'mdi-react/CommentTextOutlineIcon';
import CommentOutlineIcon from 'mdi-react/CommentOutlineIcon';
import EditOutlineIcon from 'mdi-react/EditOutlineIcon';
import DeleteOutlineIcon from 'mdi-react/DeleteOutlineIcon';
import MagnifyIcon from 'mdi-react/MagnifyIcon';
import { toast } from 'react-toastify';
import DotsHorizontalIcon from 'mdi-react/DotsHorizontalIcon';
import CashRefundIcon from 'mdi-react/CashRefundIcon';
import t, { partial } from '../../../util/translation/translation';
import Checkbox from '../../../shared/components/custom/Checkbox';
import useModal from '../../../shared/hooks/useModal';
import ShareModal from '../../../shared/components/custom/ShareModal';
import DeleteModal from '../../../shared/components/custom/DeleteModal';
import InviteParticipantsModal from './InviteParticipantsModel';
import Api from '../../../util/api';
import EditAnswersModal from './EditAnswersModal';
import CommentModal from './CommentModal';
import SortDirection from '../../../shared/components/custom/SortDirection';
import FormCard from '../../../shared/components/custom/FormCard';
import SquareIconButton from '../../../shared/components/custom/SquareIconButton';
import { formatDateTime } from '../../../util/date-and-time';
import InvitesCard from './InvitesCard';
import RefundModal from './RefundModal';
import EventPaymentsPanel from './EventPaymentsPanel';
import { isPaidEvent, participantPayment, placesSummary } from '../payments';

// Passed to the delete modal instead of a participant id when the selected
// rows are removed in bulk.
const DELETE_SELECTED = 'selected';

const Stepper = ({ value, onChange, max, label }) => (
  <div className="count-stepper" role="group" aria-label={label}>
    <button
      type="button"
      className="count-stepper__btn"
      onClick={() => onChange(value - 1)}
      disabled={value <= 0}
      aria-label="-1"
    >
      <MinusIcon />
    </button>
    <span className="count-stepper__value">
      {value}
      {max !== undefined && <span className="count-stepper__max">/{max}</span>}
    </span>
    <button
      type="button"
      className="count-stepper__btn"
      onClick={() => onChange(value + 1)}
      disabled={max !== undefined && value >= max}
      aria-label="+1"
    >
      <PlusIcon />
    </button>
  </div>
);

Stepper.propTypes = {
  value: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
  max: PropTypes.number,
  label: PropTypes.string,
};

Stepper.defaultProps = {
  max: undefined,
  label: undefined,
};

const ProgressStat = ({ label, value, total, note }) => {
  const percent = total ? Math.min(100, (value / total) * 100) : 0;
  return (
    <div className="participant-stats__item">
      <span className="participant-stats__label">{label}</span>
      <span className="participant-stats__value">
        {value}
        {total > 0 && (
          <span className="participant-stats__total"> / {total}</span>
        )}
      </span>
      <div className="participant-stats__bar">
        <div style={{ width: `${percent}%` }} />
      </div>
      {note && <span className="participant-stats__note">{note}</span>}
    </div>
  );
};

ProgressStat.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.number.isRequired,
  total: PropTypes.number.isRequired,
  note: PropTypes.string,
};

ProgressStat.defaultProps = {
  note: null,
};

const EventsPariticipantForm = ({
  companies,
  setCompanies,
  handleInviteParticipant,
  inviteModelVisible,
  setInviteModelVisible,
  changeGuestAmount,
  participants,
  setLoading,
  getPaticipants,
  currentParticipant,
  setCurrentParticipant,
  setParticipants,
  markParticipantAttendance,
  event,
  exportParticpants,
  exportParticpantsXLSX,
  questions,
  userAnswers,
  saveAnswers,
  setMaxGuestAmount,
  maxGuestAmount,
  deletedParticipants,
  setDeletedParticipants,
  eventID,
  payments,
  eventCounts,
  currency,
  onPaymentsChanged,
}) => {
  const p = partial('EventParticipantsPage');
  const ep = partial('EventPayments');
  const qst = partial('question');
  const s = partial('shared');
  const me = useSelector((state) => state.admin.me);
  const hasCompanyAccess = !!me?.permissions?.includes('access-companies');
  const [handleModal, visible, deleteId, comment, setComment] = useModal();
  const [showEditModal, setShowEditModal] = useState(false);
  const [userQuestions, setUserQuestions] = useState([]);
  // Saved answers the edit form doesn't show (e.g. a question since switched
  // to guests only); sent back unchanged so saving doesn't delete them
  const hiddenAnswersRef = useRef([]);
  const [questionAndAnswerIds, setQuestionAndAnswerIds] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [visibleCompany, setVisibleCompany] = useState(null);
  const [participantSearch, setParticipantSearch] = useState('');
  const [participantType, setParticipantType] = useState('person');
  const [invites, setInvites] = useState([]);
  const [selectedParticipantForComment, setSelectedParticipantForComment] =
    useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [sort, setSort] = useState({ column: 'name', order: 0 });
  // The payment being refunded: { transaction, name }
  const [refundTarget, setRefundTarget] = useState(null);
  // A payment column once the event has a price, or anyone has paid
  const showPayments =
    isPaidEvent(event) || participants.some((item) => !!item.paymentMethod);
  const paymentOf = (participant) =>
    participantPayment(participant, payments.transactionsById, currency);
  const totalParticipantCount = useMemo(
    () =>
      participants.reduce((tot, curr) => {
        return tot + curr.friendAmount + 1;
      }, 0),
    [participants]
  );
  const attendedCount = useMemo(
    () =>
      participants.reduce((tot, curr) => {
        return tot + curr.attendedAmount;
      }, 0),
    [participants]
  );
  const companyParticipants = useMemo(
    () => participants.filter((item) => item.type === 'COMPANY'),
    [participants]
  );
  const guestPartcipants = useMemo(
    () => participants.filter((item) => item.type === 'GUEST'),
    [participants]
  );
  const userPartcipants = useMemo(
    () => participants.filter((item) => item.type === 'USER'),
    [participants]
  );
  const markedAttendantIds = useMemo(
    () => participants.filter((item) => item.toggled).map((item) => item.id),
    [participants]
  );
  const markedCompanyAttendantIds = useMemo(
    () =>
      companies
        .filter(({ toggled }) => toggled)
        .map(({ id }) =>
          participants.filter(({ companyId }) => companyId === id)
        )
        .flat()
        .map((item) => item.id),
    [companies, participants]
  );
  const selectedParticipantIds =
    participantType === 'person'
      ? markedAttendantIds
      : markedCompanyAttendantIds;
  const canSelect =
    participantType === 'person' || participantType === 'company';
  const search = participantSearch.trim().toLowerCase();

  const removeParticipant = async (payload, participantID) => {
    setLoading(true);
    try {
      await Api.events.removeParticipants(payload, participantID);
      const { userId } = participants.find(
        (participant) => participant.id === participantID
      );
      await Api.questions.deleteAllAnswersInEvent(event.id, [userId]);
      getPaticipants();
      setLoading(false);
      handleModal();
    } catch (err) {
      setLoading(false);
      toast.error(err || `${p('participantDeletingERROR')}'!'`);
    }
  };
  useEffect(() => {
    if (eventID) {
      fetchInvites();
    }
  }, [eventID]);

  const deleteMultipleParticipants = async (ids) => {
    setLoading(true);
    try {
      await Promise.all(ids.map((id) => Api.events.removeParticipants({}, id)));
      const matchedUsers = participants
        .filter((participant) => ids.includes(participant.id))
        .map((participant) => participant.userId);
      await Api.questions.deleteAllAnswersInEvent(event.id, [...matchedUsers]);
      getPaticipants();
      setLoading(false);
      handleModal();
    } catch (err) {
      setLoading(false);
      toast.error(err || `${p('participantDeletingERROR')}'!'`);
    }
  };
  const toggleParticipant = (participantId) => {
    const participantIndex = participants.findIndex(
      ({ id }) => id === participantId
    );
    const _participants = [...participants];
    _participants[participantIndex].toggled =
      !_participants[participantIndex].toggled;
    setParticipants(_participants);
  };

  const fetchInvites = async () => {
    try {
      setLoading(true);
      const _inviteData = await Api.events.fetchInvites(eventID);
      setInvites(_inviteData);
      setLoading(false);
    } catch (err) {
      setLoading(false);
      toast.error(p('emailInviteError'));
    }
  };
  const sortTable = (array) => {
    return array.slice().sort((a, b) => {
      let fieldA;
      let fieldB;
      if (sort.column === 'name') {
        fieldA = `${a.firstname} ${a.lastname}`;
        fieldB = `${b.firstname} ${b.lastname}`;
      } else if (sort.column === 'registeredAt') {
        return sort.order === 1
          ? new Date(a.createdAt) - new Date(b.createdAt)
          : new Date(b.createdAt) - new Date(a.createdAt);
      }

      if (sort.order === 1) {
        return fieldA.localeCompare(fieldB);
      } else {
        return fieldB.localeCompare(fieldA);
      }
    });
  };

  const hanldeHeaderClick = (header) => {
    let direction;
    if (header === sort.column) {
      if (sort.order === 1) {
        direction = 0;
      } else {
        direction = 1;
      }
    } else {
      direction = 0;
    }
    setSort({ column: header, order: direction });
  };

  const sortableHeader = (column, label) => (
    <th className="sortable" onClick={() => hanldeHeaderClick(column)}>
      {label}
      <SortDirection active={sort.column === column} direction={sort.order} />
    </th>
  );

  const handleEditPressed = (participant) => {
    setModalVisible(true);
    setSelectedParticipantForComment(participant);
  };

  const handleCloseModal = () => {
    setSelectedParticipantForComment(null);
    setModalVisible(false);
  };

  const handleEditParticipant = (participant) => {
    setCurrentParticipant(participant);
    setInviteModelVisible(true);
  };

  // Active participants carry a company object, cancelled ones just its name.
  const companyName = (participant) =>
    participant.company?.name ||
    (typeof participant.company === 'string' ? participant.company : '');

  const matchesSearch = (participant, keys) =>
    !search ||
    keys.some((key) => {
      const value =
        key === 'company' ? companyName(participant) : participant[key];
      if (!value) return false;
      return (key === 'createdAt' ? formatDateTime(value) : value.toString())
        .toLowerCase()
        .includes(search);
    });

  // A full-width row for when a table has nothing to show - either nothing
  // exists yet, or the search filtered every row out.
  const renderEmptyRow = (colSpan, emptyText) => (
    <tr>
      <td colSpan={colSpan} className="text-muted text-center">
        {search
          ? p('noSearchResults', { query: participantSearch })
          : emptyText}
      </td>
    </tr>
  );

  // Phone and company sit under the name, rather than in columns of their
  // own that are mostly empty for private signups.
  const renderNameDetails = (participant, showCompany = true) => {
    const details = [
      participant.phoneNumber,
      showCompany && companyName(participant),
    ].filter(Boolean);
    // Names of the guests they bring; hover shows the email when given
    const namedGuests = (participant.guests || []).filter((g) => g?.name);
    return (
      <>
        {details.length > 0 && (
          <small className="text-muted d-block">{details.join(' · ')}</small>
        )}
        {namedGuests.length > 0 && (
          <small
            className="text-muted d-block"
            title={namedGuests
              .map((g) => (g.email ? `${g.name} <${g.email}>` : g.name))
              .join('\n')}
          >
            {p('guestNamesLine', {
              names: namedGuests.map((g) => g.name).join(', '),
            })}
          </small>
        )}
      </>
    );
  };

  const renderNameCell = (participant, showCompany = true) => (
    <td className="table-stack__primary">
      {/* The guest dot is centred against both lines of the name block. */}
      <div className="d-flex align-items-center" style={{ gap: 8 }}>
        {participant.type === 'GUEST' && (
          <>
            <span
              id={`guest-${participant.id}`}
              className="status-dot status-dot--on participant-guest-icon"
              aria-label={s('guest')}
            >
              <AccountQuestionOutlineIcon />
            </span>
            <UncontrolledTooltip
              placement="top"
              target={`guest-${participant.id}`}
            >
              {s('guest')}
            </UncontrolledTooltip>
          </>
        )}
        <div className="participant-name">
          <span>{`${participant.firstname} ${participant.lastname}`}</span>
          {renderNameDetails(participant, showCompany)}
        </div>
      </div>
    </td>
  );

  const renderGuestStepper = (participant) => (
    <Stepper
      value={participant.friendAmount}
      label={s('guests')}
      onChange={(amount) => changeGuestAmount(amount, participant)}
    />
  );

  const renderAttendance = (participant) => {
    const total = participant.friendAmount + 1;
    return (
      <div className="d-flex align-items-center" style={{ gap: 8 }}>
        <Stepper
          value={participant.attendedAmount}
          max={total}
          label={p('attended')}
          onChange={(amount) =>
            markParticipantAttendance(participant.id, amount)
          }
        />
        <SquareIconButton
          icon={<AccountMultipleCheckIcon />}
          title={p('markAllAttended')}
          id={`attend-all-${participant.id}`}
          disabled={participant.attendedAmount >= total}
          onClick={() => markParticipantAttendance(participant.id, total)}
        />
      </div>
    );
  };

  const renderCommentButton = (participant) => (
    <SquareIconButton
      icon={
        participant.additionalNotes ? (
          <CommentTextOutlineIcon />
        ) : (
          <CommentOutlineIcon />
        )
      }
      active={!!participant.additionalNotes}
      title={t('EventsPage.addNotes')}
      id={`comment-${participant.id}`}
      onClick={() => handleEditPressed(participant)}
    />
  );

  const renderPaymentCell = (participant) => {
    const payment = paymentOf(participant);
    return (
      <td data-label={ep('payment')}>
        <div className="event-payments__status">
          {payment.key === 'none' ? (
            <span className="text-muted">-</span>
          ) : (
            <Badge color={payment.color}>{payment.label}</Badge>
          )}
          {participant.paidLate && (
            <>
              <Badge
                color="warning"
                id={`paid-late-${participant.id}`}
                className="event-payments__late"
              >
                {ep('paidLate')}
              </Badge>
              <UncontrolledTooltip
                placement="top"
                target={`paid-late-${participant.id}`}
              >
                {ep('paidLateHint')}
              </UncontrolledTooltip>
            </>
          )}
        </div>
      </td>
    );
  };

  const renderRefundButton = (participant) => {
    const payment = paymentOf(participant);
    if (!payment.refundable) return null;
    return (
      <SquareIconButton
        icon={<CashRefundIcon />}
        title={ep('refund')}
        tone="danger"
        id={`refund-${participant.id}`}
        onClick={() =>
          setRefundTarget({
            transaction: payment.transaction,
            name: `${participant.firstname} ${participant.lastname}`,
          })
        }
      />
    );
  };

  const isFullyAttended = (participant) =>
    participant.attendedAmount >= participant.friendAmount + 1;

  const renderDeletedParticipants = () => {
    const rows = sortTable(deletedParticipants).filter((participant) =>
      matchesSearch(participant, [
        'firstname',
        'lastname',
        'phoneNumber',
        'company',
      ])
    );
    if (!rows.length) {
      return renderEmptyRow(2, p('noCancellations'));
    }
    return rows.map((participant) => (
      <tr key={`renderDeleted-${participant.id}`}>
        {renderNameCell(participant)}
        <td data-label={p('cancelledAt')}>
          {participant?.deletedAt ? formatDateTime(participant.deletedAt) : '-'}
        </td>
      </tr>
    ));
  };

  const renderParticipants = () => {
    const rows = sortTable([
      ...userPartcipants,
      ...guestPartcipants,
      ...companyParticipants,
    ]).filter((participant) =>
      matchesSearch(participant, [
        'firstname',
        'lastname',
        'phoneNumber',
        'company',
        'createdAt',
      ])
    );
    if (!rows.length) {
      return renderEmptyRow(showPayments ? 7 : 6, p('noParticipants'));
    }
    return rows.map((participant) => (
      <tr
        key={`renderParticipants-${participant.id}`}
        className={isFullyAttended(participant) ? 'table-row--done' : ''}
      >
        <td className="table-col--check">
          <Checkbox
            index={1}
            toggled={participant.toggled}
            single
            handleToggle={() => toggleParticipant(participant.id)}
          />
        </td>
        {renderNameCell(participant)}
        <td data-label={p('registeredAt')}>
          {formatDateTime(participant?.createdAt) || '-'}
        </td>
        <td data-label={s('guests')}>{renderGuestStepper(participant)}</td>
        <td data-label={p('attended')}>{renderAttendance(participant)}</td>
        {showPayments && renderPaymentCell(participant)}
        <td className="table-stack__actions">
          <div className="icon-actions">
            {renderCommentButton(participant)}
            {renderRefundButton(participant)}
            <SquareIconButton
              icon={<EditOutlineIcon />}
              title={s('edit')}
              id={`edit-${participant.id}`}
              onClick={() => handleEditParticipant(participant)}
            />
            <SquareIconButton
              icon={<DeleteOutlineIcon />}
              title={s('delete')}
              tone="danger"
              id={`delete-${participant.id}`}
              onClick={() => handleModal(participant.id)}
            />
          </div>
        </td>
      </tr>
    ));
  };

  const getAnswer = (eventQuestion, userQuestion) => {
    const answer = userQuestion?.answer.find(
      (userAnswer) =>
        userAnswer.id === eventQuestion.id &&
        !!userAnswer.guest === !!eventQuestion.guest &&
        (!eventQuestion.guest ||
          userAnswer.guestIndex === eventQuestion.guestIndex)
    );
    if (answer) {
      return answer.answer;
    }
    return eventQuestion.question_type === 'checkbox' ? [] : '';
  };

  const handleEditAnswers = (userId, guestCount) => {
    const questionnaire = userAnswers.find((a) => a.userId === userId);
    setShowEditModal(true);

    (async () => {
      const res = await Api.questions.fetchDomainSpecificQuestions(event.id);

      const allQuestions = res?.data?.question || [];

      // Events saved with guest fields ask some questions per guest
      // (askFor guests/both) and store them with a guestIndex
      if (res?.data?.guestFields) {
        const asksParticipant = (q) => q.askFor !== 'guests';
        const asksGuests = (q) =>
          q.question_type !== 'reservation' &&
          (q.askFor === 'guests' || q.askFor === 'both');

        const participantQuestions = allQuestions
          .filter(asksParticipant)
          .map((question) => ({
            ...question,
            answer: getAnswer(question, questionnaire),
          }));
        const perGuestQuestions = [];
        for (let i = 0; i < guestCount; i++) {
          allQuestions.filter(asksGuests).forEach((question) => {
            const entry = { ...question, guest: true, guestIndex: i };
            perGuestQuestions.push({
              ...entry,
              // The form keys fields by id, so each guest needs its own;
              // handleSubmit restores the real id and question text
              id: `${question.id}__guest${i}`,
              sourceId: question.id,
              originalQuestion: question.question,
              question: `${question.question} (${s('guest')} ${i + 1})`,
              answer: getAnswer(entry, questionnaire),
            });
          });
        }
        // Answers given under the old per-guest name/details setup
        const legacyGuestAnswers =
          questionnaire?.answer.filter(
            (answer) => answer.guest && !Number.isInteger(answer.guestIndex)
          ) || [];
        hiddenAnswersRef.current =
          questionnaire?.answer.filter(
            (answer) =>
              !legacyGuestAnswers.includes(answer) &&
              !participantQuestions.some(
                (q) => !answer.guest && q.id === answer.id
              ) &&
              !perGuestQuestions.some(
                (q) =>
                  answer.guest &&
                  q.sourceId === answer.id &&
                  q.guestIndex === answer.guestIndex
              )
          ) || [];

        setUserQuestions([
          ...participantQuestions,
          ...perGuestQuestions,
          ...legacyGuestAnswers,
        ]);
        setQuestionAndAnswerIds({
          questionId: res?.data?.id,
          answerId: questionnaire?.id,
        });
        setSelectedUserId(userId);
        return;
      }

      hiddenAnswersRef.current = [];
      const eventQuestions = allQuestions.map((question) => ({
        ...question,
        answer: getAnswer(question, questionnaire),
      }));

      let guestQuestions = [];
      if (questionnaire) {
        guestQuestions =
          questionnaire?.answer.filter((question) => question.guest) || [];
      } else {
        let id = 1;
        for (let i = 1; i <= guestCount; i++) {
          guestQuestions.push(
            {
              id,
              question: `Name of guest ${i}`,
              question_type: 'text_field',
              guest: true,
              answer: '',
            },
            {
              id: id + 1,
              question: `Details of guest ${i}`,
              question_type: 'text_field',
              guest: true,
              answer: '',
            }
          );
          id += 2;
        }
      }

      setUserQuestions([...eventQuestions, ...guestQuestions]);
      setQuestionAndAnswerIds({
        questionId: res?.data?.id,
        answerId: questionnaire?.id,
      });
      setSelectedUserId(userId);
    })();
  };

  const toggleCompany = (companyId) => {
    const companyIndex = companies.findIndex(({ id }) => id === companyId);
    const _companies = [...companies];
    _companies[companyIndex].toggled = !_companies[companyIndex].toggled;
    setCompanies(_companies);
  };
  const renderCompanyParticipants = () => {
    const filteredCompanyIds = [
      ...new Set(companyParticipants.map((item) => item.companyId)),
    ];
    const participantCompaniesArray = filteredCompanyIds.map((companyId) => {
      const participantsForCompany = companyParticipants.filter(
        (employee) => employee.companyId === companyId
      );
      const foundCompany = companies.find((comp) => comp.id === companyId);
      const totalParticipantAmount = participantsForCompany.reduce(
        (tot, curr) => tot + curr.friendAmount + 1,
        0
      );
      return {
        ...foundCompany,
        participants: participantsForCompany,
        totalParticipantAmount,
        guestAmount: totalParticipantAmount - participantsForCompany.length,
        attendedAmount: participantsForCompany.reduce(
          (tot, curr) => tot + curr.attendedAmount,
          0
        ),
      };
    });
    const rows = participantCompaniesArray.filter(
      (company) => !search || company.name?.toLowerCase().includes(search)
    );
    if (!rows.length) {
      return renderEmptyRow(showPayments ? 7 : 6, p('noParticipants'));
    }
    return rows.map((company) => {
      const expanded = visibleCompany === company.id;
      const logo = company?.imagesUrls?.sqNormal;
      return (
        <React.Fragment key={`renderCompany-${company.id}`}>
          <tr>
            <td className="table-col--check">
              <Checkbox
                handleToggle={() => toggleCompany(company.id)}
                toggled={company.toggled}
                single
                index={1}
              />
            </td>
            <td className="table-stack__primary">
              <button
                type="button"
                className="participant-company-toggle"
                aria-expanded={expanded}
                onClick={() => setVisibleCompany(expanded ? null : company.id)}
              >
                {expanded ? <ChevronDownIcon /> : <ChevronRightIcon />}
                {logo && logo !== 'NULL' ? (
                  <img className="company__profile__avatar" src={logo} alt="" />
                ) : (
                  <span className="company__profile__avatar_placeholder">
                    {company?.name?.[0]?.toUpperCase()}
                  </span>
                )}
                <span>{company?.name}</span>
              </button>
            </td>
            <td data-label={`${s('participants')} / ${p('maxParticipants')}`}>
              {company.totalParticipantAmount}
              {company.maxPartcipants ? (
                <span className="text-muted"> / {company.maxPartcipants}</span>
              ) : null}
            </td>
            <td data-label={s('guests')}>{company.guestAmount}</td>
            <td data-label={p('attended')}>
              {company.attendedAmount}
              <span className="text-muted">
                {' '}
                / {company.totalParticipantAmount}
              </span>
            </td>
            {showPayments && <td />}
            <td className="table-stack__actions" />
          </tr>
          {expanded &&
            company.participants.map((participant) => (
              <tr
                key={`renderParticipant-${participant.id}-${company.id}`}
                className={`table-row--nested${
                  isFullyAttended(participant) ? ' table-row--done' : ''
                }`}
              >
                <td />
                {renderNameCell(participant, false)}
                <td />
                <td data-label={s('guests')}>
                  {renderGuestStepper(participant)}
                </td>
                <td data-label={p('attended')}>
                  {renderAttendance(participant)}
                </td>
                {showPayments && renderPaymentCell(participant)}
                <td className="table-stack__actions">
                  <div className="icon-actions">
                    {renderCommentButton(participant)}
                    {renderRefundButton(participant)}
                    <SquareIconButton
                      icon={<DeleteOutlineIcon />}
                      title={s('delete')}
                      tone="danger"
                      id={`delete-company-${participant.id}`}
                      onClick={() => handleModal(participant.id)}
                    />
                  </div>
                </td>
              </tr>
            ))}
        </React.Fragment>
      );
    });
  };
  const participantMasterToggle = (_, toggled) => {
    const _participants = participants.map((item) => ({ ...item, toggled }));
    const _deletedParticipants = deletedParticipants.map((item) => ({
      ...item,
      toggled,
    }));
    setParticipants(_participants);
    setDeletedParticipants(_deletedParticipants);
  };
  const companyMasterToggle = (_, toggled) => {
    const eligableCompanies = [
      ...new Set(participants.map((item) => item.companyId)),
    ].filter((item) => item);
    const _companies = companies.map((item) => ({
      ...item,
      toggled: eligableCompanies.includes(item.id) ? toggled : item.toggled,
    }));
    setCompanies(_companies);
  };

  const renderDeletedTable = () => (
    <Table responsive striped className="table--roomy table--stack">
      <thead>
        <tr>
          {sortableHeader('name', s('name'))}
          <th>{p('cancelledAt')}</th>
        </tr>
      </thead>
      <tbody>{renderDeletedParticipants()}</tbody>
    </Table>
  );

  const renderParticipantsTable = () => {
    const masterToggled =
      participants.length > 0 && participants.every(({ toggled }) => toggled);
    return (
      <Table responsive striped className="table--roomy table--stack">
        <thead>
          <tr>
            <th className="table-col--check">
              <Checkbox
                handleToggle={participantMasterToggle}
                toggled={masterToggled}
                single
                index={1}
              />
            </th>
            {sortableHeader('name', s('name'))}
            {sortableHeader('registeredAt', p('registeredAt'))}
            <th>{s('guests')}</th>
            <th>{p('attended')}</th>
            {showPayments && <th>{ep('payment')}</th>}
            <th />
          </tr>
        </thead>
        <tbody>{renderParticipants()}</tbody>
      </Table>
    );
  };

  // ---- Questions view: one expandable row per participant ----

  // The questions asked of participants themselves; answers given per guest
  // (guest + guestIndex) are shown under each guest instead
  const isPerGuest = (entry) =>
    !!entry?.guest && Number.isInteger(entry?.guestIndex);
  const participantQuestions = useMemo(() => {
    const seen = new Set();
    return (questions || []).filter((q) => {
      if (isPerGuest(q) || seen.has(q.id)) return false;
      seen.add(q.id);
      return true;
    });
  }, [questions]);

  const submissionOf = (participant) =>
    participant.userId
      ? (userAnswers || []).find((a) => a.userId === participant.userId)
      : null;

  const hasAnswer = (value) =>
    Array.isArray(value)
      ? value.length > 0
      : value !== undefined && value !== null && value !== '';

  const formatAnswer = (entry, question) => {
    const value = entry?.answer;
    if (!hasAnswer(value)) return null;
    if (
      question?.question_type === 'reservation' ||
      entry?.question_type === 'reservation'
    ) {
      const options = question?.options || entry?.options || [];
      const selected = typeof value === 'object' ? value.selectedItem : value;
      const option = options.find((o) => o.id === selected);
      const qty = typeof value === 'object' ? value.quantity || 1 : 1;
      return `${option ? option.title : selected} (×${qty})`;
    }
    if (Array.isArray(value)) return value.join(', ');
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  };

  const answerEntryFor = (entries, question) =>
    entries.find((e) => e.id === question.id) ||
    entries.find((e) => e.question === question.question);

  const answersOf = (participant) => {
    const entries = (submissionOf(participant)?.answer || []).filter(
      (e) => !isPerGuest(e)
    );
    return participantQuestions.map((q) => ({
      id: q.id,
      question: q.question,
      answer: formatAnswer(answerEntryFor(entries, q), q),
    }));
  };

  // The event's saved form: its id is needed to save answers, and its
  // questions say which ones are asked of each guest
  const [eventForm, setEventForm] = useState(null);
  useEffect(() => {
    if (participantType !== 'questions' || !event?.id) return;
    Api.questions
      .fetchDomainSpecificQuestions(event.id)
      .then((res) => setEventForm(res?.data || null))
      .catch(() => setEventForm(null));
  }, [participantType, event?.id, userAnswers]);

  // Questions set to Guests or Both (reservations are booked once per group)
  const guestQuestions = useMemo(
    () =>
      eventForm?.guestFields
        ? (eventForm.question || []).filter(
            (q) =>
              q.question_type !== 'reservation' &&
              (q.askFor === 'guests' || q.askFor === 'both')
          )
        : [],
    [eventForm]
  );

  const guestsOf = (participant) => {
    const guestEntries = (submissionOf(participant)?.answer || []).filter(
      isPerGuest
    );
    return Array.from({ length: participant.friendAmount || 0 }, (_, i) => {
      const own = guestEntries.filter((e) => e.guestIndex === i);
      // Every question asked of guests, plus anything answered under a
      // question that has since changed
      const questionsForGuest = [
        ...guestQuestions,
        ...own.filter((e) => !guestQuestions.some((q) => q.id === e.id)),
      ];
      return {
        index: i,
        name: participant.guests?.[i]?.name || '',
        email: participant.guests?.[i]?.email || '',
        answers: questionsForGuest.map((q) => {
          const entry = own.find((e) => e.id === q.id);
          return {
            id: q.id,
            question: q.question,
            questionData: q,
            raw: entry?.answer,
            answer: formatAnswer(entry, q),
          };
        }),
      };
    });
  };

  const [expandedRows, setExpandedRows] = useState([]);
  const toggleRow = (id) =>
    setExpandedRows((rows) =>
      rows.includes(id) ? rows.filter((r) => r !== id) : [...rows, id]
    );

  // The guest being edited: { participantId, index, name, email, answers }
  const [guestEdit, setGuestEdit] = useState(null);
  const [guestEditError, setGuestEditError] = useState('');
  const [savingGuest, setSavingGuest] = useState(false);
  // The guest waiting for the delete confirmation: { participant, index }
  const [guestToDelete, setGuestToDelete] = useState(null);

  const EDITABLE_TYPES = ['text_field', 'radio_button', 'dropdown', 'checkbox'];

  const startGuestEdit = (participant, guest) => {
    setGuestEditError('');
    const answers = {};
    guest.answers.forEach((a) => {
      const type = a.questionData?.question_type;
      if (!EDITABLE_TYPES.includes(type)) return;
      if (type === 'checkbox') {
        answers[a.id] = Array.isArray(a.raw) ? a.raw : [];
      } else {
        answers[a.id] =
          typeof a.raw === 'object' && a.raw !== null
            ? a.raw.value || ''
            : a.raw || '';
      }
    });
    setGuestEdit({
      participantId: participant.id,
      index: guest.index,
      name: guest.name,
      email: guest.email,
      answers,
      initialAnswers: answers,
    });
  };

  // The participant's guest list as stored: one entry per guest they bring
  const guestListOf = (participant) =>
    Array.from({ length: participant.friendAmount || 0 }, (_, i) => ({
      name: participant.guests?.[i]?.name || null,
      email: participant.guests?.[i]?.email || null,
    }));

  const updateParticipantGuests = async (participant, changes) => {
    // Guests are sent explicitly, so the server keeps exactly this list
    const { guests, ...rest } = participant;
    const res = await Api.events.updateParticipant(
      { ...rest, ...changes },
      participant.id
    );
    return Array.isArray(res?.data?.guests) ? res.data.guests : changes.guests;
  };

  // Answers are saved as the participant's whole list, like the edit modal
  const saveAnswerList = (participant, answerList) => {
    const submission = submissionOf(participant);
    const payload = {
      questionId: eventForm?.id,
      answer: answerList,
    };
    if (submission?.id) {
      payload.answerId = submission.id;
    } else {
      payload.userId = participant.userId;
    }
    return saveAnswers(payload);
  };

  const saveGuestDetails = async (participant) => {
    const email = guestEdit.email.trim();
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setGuestEditError(p('invalidGuestEmail'));
      return;
    }
    const current = participant.guests?.[guestEdit.index] || {};
    const detailsChanged =
      (current.name || '') !== guestEdit.name.trim() ||
      (current.email || '') !== email;
    const answersChanged =
      JSON.stringify(guestEdit.answers) !==
      JSON.stringify(guestEdit.initialAnswers);

    setSavingGuest(true);
    try {
      if (detailsChanged) {
        const nextGuests = guestListOf(participant).map((guest, i) =>
          i === guestEdit.index
            ? { name: guestEdit.name.trim() || null, email: email || null }
            : guest
        );
        const savedGuests = await updateParticipantGuests(participant, {
          guests: nextGuests,
        });
        setParticipants(
          participants.map((item) =>
            item.id === participant.id ? { ...item, guests: savedGuests } : item
          )
        );
      }
      if (answersChanged && participant.userId) {
        const editedIds = Object.keys(guestEdit.answers);
        const kept = (submissionOf(participant)?.answer || []).filter(
          (e) =>
            !(
              isPerGuest(e) &&
              e.guestIndex === guestEdit.index &&
              editedIds.includes(String(e.id))
            )
        );
        const edited = guestQuestions
          .filter((q) => editedIds.includes(String(q.id)))
          .map((q) => ({
            ...q,
            guest: true,
            guestIndex: guestEdit.index,
            answer: guestEdit.answers[q.id],
          }));
        await saveAnswerList(participant, [...kept, ...edited]);
      }
      setGuestEdit(null);
      toast.success(p('guestDetailsSaved'));
    } catch {
      toast.error(p('guestDetailsSaveError'));
    } finally {
      setSavingGuest(false);
    }
  };

  // Removes one guest: one fewer in the participant's count, that guest's
  // details and answers gone, and the guests after it move up one place
  const deleteGuest = async () => {
    const { participant, index } = guestToDelete;
    setGuestToDelete(null);
    const friendAmount = Math.max(0, (participant.friendAmount || 0) - 1);
    try {
      const nextGuests = guestListOf(participant).filter((_, i) => i !== index);
      await updateParticipantGuests(participant, {
        friendAmount,
        // Can't have more people attended than the group now has
        attendedAmount: Math.min(
          participant.attendedAmount || 0,
          friendAmount + 1
        ),
        guests: nextGuests,
      });
      const entries = submissionOf(participant)?.answer || [];
      if (entries.some(isPerGuest)) {
        await saveAnswerList(
          participant,
          entries
            .filter((e) => !(isPerGuest(e) && e.guestIndex === index))
            .map((e) =>
              isPerGuest(e) && e.guestIndex > index
                ? { ...e, guestIndex: e.guestIndex - 1 }
                : e
            )
        );
      } else {
        await getPaticipants();
      }
      toast.success(p('guestRemoved'));
    } catch {
      toast.error(p('guestRemoveError'));
    }
  };

  const questionRows = () =>
    [...userPartcipants, ...guestPartcipants, ...companyParticipants].filter(
      (participant) =>
        matchesSearch(participant, [
          'firstname',
          'lastname',
          'phoneNumber',
          'company',
        ]) ||
        // Also find people by what they answered
        (!!search &&
          answersOf(participant).some((a) =>
            a.answer?.toLowerCase().includes(search)
          ))
    );

  const renderAnswerList = (answers) => (
    <dl className="pa-answers">
      {answers.map((a) => (
        <div key={a.id || a.question}>
          <dt>{a.question}</dt>
          <dd className={a.answer ? '' : 'pa-answers__empty'}>
            {a.answer || p('noAnswer')}
          </dd>
        </div>
      ))}
    </dl>
  );

  const setEditAnswer = (id, value) =>
    setGuestEdit((edit) => ({
      ...edit,
      answers: { ...edit.answers, [id]: value },
    }));

  const renderAnswerInput = (a) => {
    const q = a.questionData || {};
    const value = guestEdit.answers[a.id];
    const choices = (q.choices || []).map((c) => c.choice ?? c);
    const inputId = `guest-answer-${guestEdit.participantId}-${guestEdit.index}-${a.id}`;
    switch (q.question_type) {
      case 'text_field':
        return (
          <input
            id={inputId}
            className="form-control form-control-sm"
            value={value}
            onChange={(e) => setEditAnswer(a.id, e.target.value)}
          />
        );
      case 'radio_button':
      case 'dropdown':
        return (
          <select
            id={inputId}
            className="form-control form-control-sm"
            value={value}
            onChange={(e) => setEditAnswer(a.id, e.target.value)}
          >
            <option value="">{p('chooseAnswer')}</option>
            {choices.map((choice) => (
              <option key={choice} value={choice}>
                {choice}
              </option>
            ))}
          </select>
        );
      case 'checkbox':
        return (
          <div className="pa-guest__checks" id={inputId}>
            {choices.map((choice) => (
              <label key={choice}>
                <input
                  type="checkbox"
                  checked={value.includes(choice)}
                  onChange={(e) =>
                    setEditAnswer(
                      a.id,
                      e.target.checked
                        ? [...value, choice]
                        : value.filter((v) => v !== choice)
                    )
                  }
                />
                {choice}
              </label>
            ))}
          </div>
        );
      default:
        // Other types (e.g. image upload) can't be edited here
        return (
          <span className={a.answer ? '' : 'pa-answers__empty'}>
            {a.answer || p('noAnswer')}
          </span>
        );
    }
  };

  const renderGuest = (participant, guest) => {
    const editing =
      guestEdit?.participantId === participant.id &&
      guestEdit?.index === guest.index;
    const label = p('guestNumber', { number: guest.index + 1 });
    const initials =
      guest.name
        .split(' ')
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0].toUpperCase())
        .join('') || `G${guest.index + 1}`;
    const empty = !guest.name && !guest.email;
    const answeredAny = guest.answers.some((a) => a.answer);

    if (editing) {
      return (
        <form
          key={guest.index}
          className="pa-guest pa-guest--editing"
          onSubmit={(e) => {
            e.preventDefault();
            saveGuestDetails(participant);
          }}
        >
          <div className="pa-guest__head pa-guest__form">
            <span className="pa-guest__avatar" aria-hidden>
              {initials}
            </span>
            <label className="pa-guest__field">
              <span>{`${label} – ${s('name')}`}</span>
              <input
                className="form-control form-control-sm"
                value={guestEdit.name}
                onChange={(e) =>
                  setGuestEdit({ ...guestEdit, name: e.target.value })
                }
              />
            </label>
            <label className="pa-guest__field pa-guest__field--wide">
              <span>{s('email')}</span>
              <input
                type="email"
                className={`form-control form-control-sm${
                  guestEditError ? ' is-invalid' : ''
                }`}
                value={guestEdit.email}
                onChange={(e) => {
                  setGuestEditError('');
                  setGuestEdit({ ...guestEdit, email: e.target.value });
                }}
              />
            </label>
            {guestEditError && (
              <span className="pa-guest__error">{guestEditError}</span>
            )}
          </div>
          {guest.answers.length > 0 && (
            <div className="pa-guest__answers pa-guest__answers--edit">
              {guest.answers.map((a) => (
                <div className="pa-guest__answer-field" key={a.id}>
                  <label
                    htmlFor={`guest-answer-${participant.id}-${guest.index}-${a.id}`}
                  >
                    {a.question}
                  </label>
                  {renderAnswerInput(a)}
                </div>
              ))}
            </div>
          )}
          <div className="pa-guest__footer">
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm mb-0"
              onClick={() => setGuestEdit(null)}
            >
              {qst('cancel')}
            </button>
            <button
              type="submit"
              className="btn btn-primary btn-sm mb-0"
              disabled={savingGuest}
            >
              {s('save')}
            </button>
          </div>
        </form>
      );
    }

    return (
      <div
        key={guest.index}
        className={`pa-guest${empty && !answeredAny ? ' pa-guest--empty' : ''}`}
      >
        <div className="pa-guest__head">
          <span className="pa-guest__avatar" aria-hidden>
            {initials}
          </span>
          <div className="pa-guest__text">
            <span className="pa-guest__name">
              {guest.name ? `${label} · ${guest.name}` : label}
            </span>
            <span className="pa-guest__email">
              {empty ? p('noGuestDetails') : guest.email || '-'}
            </span>
          </div>
          <div className="icon-actions">
            {empty && !answeredAny ? (
              <button
                type="button"
                className="btn btn-outline-primary btn-sm mb-0"
                onClick={() => startGuestEdit(participant, guest)}
              >
                {p('addGuestDetails')}
              </button>
            ) : (
              <SquareIconButton
                icon={<EditOutlineIcon />}
                title={p('editGuest')}
                id={`edit-guest-${participant.id}-${guest.index}`}
                onClick={() => startGuestEdit(participant, guest)}
              />
            )}
            <SquareIconButton
              icon={<DeleteOutlineIcon />}
              title={p('removeGuest')}
              tone="danger"
              id={`remove-guest-${participant.id}-${guest.index}`}
              onClick={() =>
                setGuestToDelete({ participant, index: guest.index })
              }
            />
          </div>
        </div>
        {guest.answers.length > 0 && (
          <div className="pa-guest__answers">
            {renderAnswerList(guest.answers)}
          </div>
        )}
      </div>
    );
  };

  const renderQuestionRow = (participant) => {
    const open = expandedRows.includes(participant.id);
    const answers = answersOf(participant);
    const answered = answers.filter((a) => a.answer).length;
    const updatedAt = submissionOf(participant)?.updatedAt;
    const guestCount = participant.friendAmount || 0;
    return (
      <div
        key={`answers-${participant.id}`}
        className={`pa-row${open ? ' pa-row--open' : ''}`}
      >
        <div className="pa-row__main">
          <button
            type="button"
            className="pa-row__toggle"
            aria-expanded={open}
            aria-label={p(open ? 'collapseRow' : 'expandRow')}
            onClick={() => toggleRow(participant.id)}
          >
            <ChevronRightIcon size={20} />
          </button>
          <button
            type="button"
            className="pa-row__name"
            onClick={() => toggleRow(participant.id)}
          >
            <span>{`${participant.firstname} ${participant.lastname}`}</span>
            {renderNameDetails(participant)}
          </button>
          <div className="pa-row__cell pa-row__cell--guests">
            {guestCount > 0 ? (
              <span className="qb-chip">
                {p('guestCount', { count: guestCount })}
              </span>
            ) : (
              <span className="text-muted">-</span>
            )}
          </div>
          <div className="pa-row__cell pa-row__cell--answered">
            <div className="mini-bar m-0">
              <div
                style={{
                  width: `${
                    answers.length ? (answered / answers.length) * 100 : 0
                  }%`,
                }}
              />
            </div>
            <span>{p('answeredOf', { answered, total: answers.length })}</span>
          </div>
          <div className="pa-row__cell pa-row__cell--updated text-muted">
            {updatedAt ? formatDateTime(updatedAt) : '-'}
          </div>
          <SquareIconButton
            icon={<EditOutlineIcon />}
            title={qst('editAnswers')}
            id={`edit-answers-${participant.id}`}
            disabled={participant.participantType === 'GUEST'}
            onClick={() =>
              handleEditAnswers(participant.userId, participant.friendAmount)
            }
          />
        </div>
        {open && (
          <div className="pa-row__detail">
            <section>
              <h4 className="option-group__title">{p('participantAnswers')}</h4>
              {renderAnswerList(answers)}
            </section>
            {guestCount > 0 && (
              <section>
                <div className="pa-row__section-head">
                  <h4 className="option-group__title">{s('guests')}</h4>
                  <span className="text-muted small">
                    {p('guestDetailsPrivacy')}
                  </span>
                </div>
                <div className="pa-guests">
                  {guestsOf(participant).map((guest) =>
                    renderGuest(participant, guest)
                  )}
                </div>
              </section>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderQuestionsTable = () => {
    const rows = questionRows();
    const guestTotal = rows.reduce(
      (sum, row) => sum + (row.friendAmount || 0),
      0
    );
    const allOpen =
      rows.length > 0 && rows.every((row) => expandedRows.includes(row.id));
    return (
      <div className="pa">
        <div className="pa__bar">
          <span className="text-muted small">
            {p('answersSummary', {
              participants: rows.length,
              guests: guestTotal,
              questions: participantQuestions.length,
            })}
          </span>
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm mb-0"
            disabled={!rows.length}
            onClick={() =>
              setExpandedRows(allOpen ? [] : rows.map((row) => row.id))
            }
          >
            {allOpen ? p('collapseAll') : p('expandAll')}
          </button>
        </div>
        {rows.length ? (
          rows.map(renderQuestionRow)
        ) : (
          <div className="pa__empty">
            {search
              ? p('noSearchResults', { query: participantSearch })
              : p('noParticipants')}
          </div>
        )}
      </div>
    );
  };

  const renderCompanyTable = () => {
    const eligableCompanies = [
      ...new Set(participants.map((item) => item.companyId)),
    ].filter((item) => item);
    const foundCompanies = companies.filter((comp) =>
      eligableCompanies.includes(comp.id)
    );
    const masterToggled =
      foundCompanies.length > 0 &&
      foundCompanies.every(({ toggled }) => toggled);
    return (
      <Table responsive striped className="table--roomy table--stack">
        <thead>
          <tr>
            <th className="table-col--check">
              <Checkbox
                handleToggle={companyMasterToggle}
                toggled={masterToggled}
                single
                index={1}
              />
            </th>
            <th>{p('company')}</th>
            <th>
              {s('participants')} / {p('maxParticipants')}
            </th>
            <th>{s('guests')}</th>
            <th>{p('attended')}</th>
            {showPayments && <th>{ep('payment')}</th>}
            <th />
          </tr>
        </thead>
        <tbody>{renderCompanyParticipants()}</tbody>
      </Table>
    );
  };

  // Views the admin can't use are left out entirely: companies need the
  // companies permission, and questions only exist if the event has any.
  const views = [
    { key: 'person', label: s('users') },
    { key: 'company', label: p('company'), hidden: !hasCompanyAccess },
    { key: 'questions', label: s('questions'), hidden: !questions?.length },
    { key: 'deleted', label: p('deletedParticipant') },
    { key: 'invites', label: p('invites') },
  ].filter(({ hidden }) => !hidden);
  const activeView = views.find(({ key }) => key === participantType);

  useEffect(() => {
    if (!activeView) {
      setParticipantType('person');
    }
  }, [activeView]);

  const changeView = (view) => {
    setParticipantType(view);
    setVisibleCompany(null);
  };

  const handleSubmit = async (values) => {
    try {
      const updatedAnswers = userQuestions.map((answer) => {
        const getValue = () => {
          if (answer.question_type === 'dropdown') {
            return values[answer.id].value || values[answer.id];
          } else if (answer.question_type === 'reservation') {
            return {
              selectedItem: values[answer.id],
              quantity: answer.answer?.quantity || 1,
            };
          } else if (answer.question_type === 'checkbox') {
            const selectedIds = Object.keys(values).filter((id) => {
              return (
                id.substring(0, id.indexOf(' ')) === answer.id && values[id]
              );
            });

            return selectedIds.map((id) => id.substring(id.indexOf(' ') + 1));
          }
          return values[answer.id];
        };

        const { sourceId, originalQuestion, ...updatedAnswer } = answer;
        updatedAnswer.answer = getValue();
        if (sourceId) {
          updatedAnswer.id = sourceId;
          updatedAnswer.question = originalQuestion;
        }
        return updatedAnswer;
      });

      const payload = {
        questionId: questionAndAnswerIds.questionId,
        answerId: questionAndAnswerIds.answerId,
        answer: [...updatedAnswers, ...hiddenAnswersRef.current],
      };

      if (questionAndAnswerIds.answerId) {
        payload.answerId = questionAndAnswerIds.answerId;
      } else {
        payload.userId = selectedUserId;
      }

      await saveAnswers(payload);

      setShowEditModal(false);
      setUserQuestions([]);
      setQuestionAndAnswerIds(null);
      setSelectedUserId(null);
    } catch (err) {
      toast.error(err || s('somethingWentWrong'));
    }
  };

  const searchInput = (
    <div className="form noWrap">
      <div className="form__form-group products-list__search">
        <input
          value={participantSearch}
          placeholder={p('searchParticipants')}
          name="search participants"
          onChange={(e) => setParticipantSearch(e.target.value)}
        />
        <MagnifyIcon />
      </div>
    </div>
  );

  return (
    <>
      <DeleteModal
        visible={!!guestToDelete}
        handleModal={() => setGuestToDelete(null)}
        type={
          guestToDelete
            ? p('guestNumber', { number: guestToDelete.index + 1 })
            : ''
        }
        modalAction={deleteGuest}
      />
      <DeleteModal
        visible={visible}
        handleModal={handleModal}
        type={s('participants')}
        modalAction={() =>
          deleteId === DELETE_SELECTED
            ? deleteMultipleParticipants(selectedParticipantIds)
            : removeParticipant(comment, deleteId)
        }
        canComment={deleteId !== DELETE_SELECTED}
        comment={comment}
        onCommentChange={setComment}
      />
      <ShareModal
        visible={modalVisible}
        title={
          selectedParticipantForComment?.additionalNotes
            ? t('EventsPage.editNotes')
            : t('EventsPage.addNotes')
        }
        size="md"
        handleModal={handleCloseModal}
      >
        <CommentModal
          selectedParticipantForComment={selectedParticipantForComment}
          handleCloseModal={handleCloseModal}
          getPaticipants={getPaticipants}
        />
      </ShareModal>
      <ShareModal
        title={
          currentParticipant
            ? `${s('edit')} ${currentParticipant.firstname} ${currentParticipant.lastname}`
            : p('inviteParticipants')
        }
        size="md"
        visible={inviteModelVisible}
        handleModal={() => {
          setInviteModelVisible(false);
          setCurrentParticipant(null);
        }}
      >
        <InviteParticipantsModal
          currentParticipant={currentParticipant}
          participantType={participantType}
          companies={companies}
          handleSubmit={handleInviteParticipant}
          event={event}
          maxGuestAmount={maxGuestAmount}
          setMaxGuestAmount={setMaxGuestAmount}
          hasCompanyAccess={hasCompanyAccess}
        />
      </ShareModal>

      <ShareModal
        title={qst('editAnswers')}
        visible={showEditModal}
        handleModal={() => {
          setShowEditModal(false);
          setUserQuestions([]);
          setQuestionAndAnswerIds(null);
          setSelectedUserId(null);
        }}
      >
        <EditAnswersModal
          userQuestions={userQuestions}
          onSubmit={handleSubmit}
        />
      </ShareModal>

      <RefundModal
        target={refundTarget}
        currency={currency}
        onClose={() => setRefundTarget(null)}
        onRefunded={() => {
          setRefundTarget(null);
          onPaymentsChanged();
        }}
      />
      <div className="participant-stats">
        <ProgressStat
          label={p('signedUp')}
          value={
            typeof eventCounts?.placesTaken === 'number'
              ? eventCounts.placesTaken
              : totalParticipantCount
          }
          total={event?.maxAttendees || 0}
          note={placesSummary(eventCounts, event?.maxAttendees)}
        />
        <ProgressStat
          label={p('attended')}
          value={attendedCount}
          total={totalParticipantCount}
        />
      </div>

      <FormCard
        title={
          <>
            <div className="form-card__switch d-none d-lg-inline-flex">
              {views.map((view) => (
                <button
                  key={view.key}
                  type="button"
                  className={`form-card__switch-btn${
                    participantType === view.key ? ' active' : ''
                  }`}
                  onClick={() => changeView(view.key)}
                >
                  {view.label}
                </button>
              ))}
            </div>
            <span className="d-lg-none">{activeView?.label}</span>
          </>
        }
        actions={
          <div className="d-flex align-items-center" style={{ gap: 8 }}>
            {searchInput}
            {canSelect && selectedParticipantIds.length > 0 && (
              <Button
                color="danger"
                size="sm"
                outline
                className="mb-0 d-none d-md-inline-block"
                onClick={() => handleModal(DELETE_SELECTED)}
              >
                {p('removeSelected', { count: selectedParticipantIds.length })}
              </Button>
            )}
            <UncontrolledDropdown>
              <DropdownToggle
                tag="button"
                type="button"
                id="export-participants"
                className="square-icon-btn"
                caret={false}
                disabled={participants?.length === 0}
              >
                <DownloadOutlineIcon />
              </DropdownToggle>
              <UncontrolledTooltip
                placement="bottom"
                target="export-participants"
              >
                {p('exportParticipants')}
              </UncontrolledTooltip>
              <DropdownMenu right className="dropdown-menu--modern">
                <DropdownItem onClick={exportParticpantsXLSX}>
                  {p('exportParticipants')} (XLSX)
                </DropdownItem>
                <DropdownItem onClick={exportParticpants}>
                  {p('exportParticipants')} (CSV)
                </DropdownItem>
              </DropdownMenu>
            </UncontrolledDropdown>
            <SquareIconButton
              icon={<AccountPlusOutlineIcon />}
              title={p('inviteParticipants')}
              id="invite-participants"
              tone="primary"
              onClick={() => setInviteModelVisible(true)}
            />
            <UncontrolledDropdown className="d-lg-none">
              <DropdownToggle
                tag="button"
                type="button"
                className="square-icon-btn"
                caret={false}
              >
                <DotsHorizontalIcon />
              </DropdownToggle>
              <DropdownMenu right className="dropdown-menu--modern">
                {views.map((view) => (
                  <DropdownItem
                    key={view.key}
                    active={participantType === view.key}
                    onClick={() => changeView(view.key)}
                  >
                    {view.label}
                  </DropdownItem>
                ))}
                {canSelect && selectedParticipantIds.length > 0 && (
                  <div className="d-md-none">
                    <DropdownItem divider />
                    <DropdownItem
                      className="text-danger"
                      onClick={() => handleModal(DELETE_SELECTED)}
                    >
                      {p('removeSelected', {
                        count: selectedParticipantIds.length,
                      })}
                    </DropdownItem>
                  </div>
                )}
              </DropdownMenu>
            </UncontrolledDropdown>
          </div>
        }
        bodyClassName="form-card__body--flush"
      >
        {participantType === 'person' && renderParticipantsTable()}
        {participantType === 'company' && renderCompanyTable()}
        {participantType === 'questions' && renderQuestionsTable()}
        {participantType === 'deleted' && renderDeletedTable()}
        {participantType === 'invites' && (
          <InvitesCard
            invites={invites}
            participantSearch={participantSearch}
            currency={currency}
          />
        )}
      </FormCard>
      <EventPaymentsPanel
        event={event}
        currency={currency}
        pending={payments.pending}
        transactions={payments.transactions}
        participants={participants}
        deletedParticipants={deletedParticipants}
        onChanged={onPaymentsChanged}
        onRefund={(transaction, name) => setRefundTarget({ transaction, name })}
        setLoading={setLoading}
      />
    </>
  );
};

EventsPariticipantForm.propTypes = {
  companies: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  payments: PropTypes.shape({
    pending: PropTypes.arrayOf(PropTypes.shape({})),
    transactions: PropTypes.arrayOf(PropTypes.shape({})),
    transactionsById: PropTypes.shape({}),
  }),
  eventCounts: PropTypes.shape({
    placesTaken: PropTypes.number,
    placesHeld: PropTypes.number,
    freePlaces: PropTypes.number,
  }),
  currency: PropTypes.string,
  onPaymentsChanged: PropTypes.func,
};

EventsPariticipantForm.defaultProps = {
  payments: { pending: [], transactions: [], transactionsById: {} },
  eventCounts: null,
  currency: '',
  onPaymentsChanged: () => {},
};

const reduxF = reduxForm({
  form: 'EventsPariticipantForm',
  enableReinitialize: true,
});

export default reduxF(EventsPariticipantForm);
